document.addEventListener('DOMContentLoaded', () => {
  // Guard against double-execution when multiple sections include this script
  if (window.__bbhInit) return;
  window.__bbhInit = true;

  // ── REVEAL ON SCROLL ──────────────────────────────────────
  const reveals = document.querySelectorAll('.bbh-reveal');
  if (reveals.length) {
    if (!('IntersectionObserver' in window)) {
      reveals.forEach((el) => el.classList.add('is-visible'));
    } else {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08 }
      );
      reveals.forEach((el) => observer.observe(el));
    }
  }

  // ── HERO BACKGROUND VIDEO — MEDIA ADAPTERS ───────────────
  // Everything below (click/mute/scroll logic) is written against this
  // small common interface so it doesn't care whether it's driving a
  // native <video> element or a YouTube iframe player. Only ONE adapter
  // should ever be attached per hero section — do not add a second,
  // separate script elsewhere that also calls play()/pause() on the
  // same media, or the two will race each other and playback will
  // appear to freeze (this happened before with a duplicate scroll
  // script on the native video — see the earlier fix).

  function createNativeVideoAdapter(video) {
    return {
      play: () => video.play().catch(() => {}),
      pause: () => video.pause(),
      mute: () => { video.muted = true; },
      unmute: () => { video.muted = false; },
      isMuted: () => video.muted,
      isPaused: () => video.paused,
      isEnded: () => video.ended,
      seekToStart: () => { video.currentTime = 0; },
      onPlay: (cb) => video.addEventListener('play', cb),
      onPause: (cb) => video.addEventListener('pause', cb),
      onEnded: (cb) => video.addEventListener('ended', cb),
      onReady: (cb) => cb(),
    };
  }

  // Loads the YouTube IFrame API once, no matter how many hero sections
  // (or theme-editor reloads) need it.
  let ytApiPromise = null;
  function loadYouTubeApi() {
    if (ytApiPromise) return ytApiPromise;
    ytApiPromise = new Promise((resolve) => {
      if (window.YT && window.YT.Player) {
        resolve(window.YT);
        return;
      }
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previous === 'function') previous();
        resolve(window.YT);
      };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    });
    return ytApiPromise;
  }

  // Forces every new YT.Player() construction on the page to happen one
  // at a time, never in the same tick. loadYouTubeApi() returns ONE
  // shared promise — so on a fresh page load, every eagerly-warmed
  // card's .then(...) callback fires together in the same microtask
  // batch the instant the YouTube API script finishes loading, no
  // matter how much the *calls* to getCardMedia() were spaced out
  // beforehand. Constructing several players back-to-back like that is
  // what was causing YouTube's IFrame API to misattribute a play
  // command to the wrong iframe — two videos audibly playing at once —
  // and it only ever showed up on the very first play after a cold page
  // load, because that's the only time this same-tick pile-up happens
  // (on later loads the API script and players are already warm/cached
  // one at a time). Routing every construction through this queue, with
  // a short settle delay between them, is what actually fixes it.
  let ytPlayerCreationChain = Promise.resolve();
  function scheduleYouTubePlayerCreation(createFn) {
    ytPlayerCreationChain = ytPlayerCreationChain.then(
      () =>
        new Promise((resolve) => {
          createFn();
          setTimeout(resolve, 350);
        })
    );
  }

  function createYouTubeAdapter(target, videoId, loop, opts) {
    opts = opts || {};
    const autoplay  = opts.autoplay !== false; // default true
    const muted     = opts.muted !== false;    // default true (hero background use)
    const controls  = !!opts.controls;         // default false (background/preview use)
    const fs        = !!opts.fs;               // default false, matches controls:0 use cases
    const disablekb = opts.disablekb !== false; // default true

    let player = null;
    let ready = false;
    // Set by destroy() — if the queued construction below hasn't run yet
    // (still waiting on the shared creation queue) by the time destroy()
    // is called, this stops it from constructing a player nobody wants
    // any more.
    let destroyed = false;
    // Tracked locally instead of always deriving it from player.isMuted().
    // mute()/unMute() are async postMessage calls into the iframe, so
    // reading player.isMuted() immediately after issuing one (as the
    // click handlers do, right after calling mute()/unmute(), to update
    // the button icon) can read the stale pre-command state — that's
    // what was making the mute button show the reversed icon. Setting
    // this synchronously the moment mute()/unmute() is called avoids
    // the race entirely.
    let mutedState = muted;
    const pending = [];
    const handlers = { play: [], pause: [], ended: [] };
    const readyHandlers = [];

    function whenReady(fn) {
      if (ready) fn();
      else pending.push(fn);
    }

    loadYouTubeApi().then((YT) => {
      scheduleYouTubePlayerCreation(() => {
        if (destroyed) return;
        player = new YT.Player(target, {
          videoId: videoId,
          host: 'https://www.youtube-nocookie.com',
          // Belt-and-suspenders alongside the CSS sizing rules — without
          // this, the iframe the API creates defaults to a 640x390 embed
          // size before any CSS is considered, which is what caused the
          // "tiny embedded video" symptom on the hero.
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: autoplay ? 1 : 0,
            mute: muted ? 1 : 0,
            controls: controls ? 1 : 0,
            loop: loop ? 1 : 0,
            playlist: loop ? videoId : undefined,
            playsinline: 1,
            modestbranding: 1,
            rel: 0,
            disablekb: disablekb ? 1 : 0,
            fs: fs ? 1 : 0,
            iv_load_policy: 3,
            cc_load_policy: 0,
          },
          events: {
            onReady: () => {
              ready = true;
              // Explicit play() alongside playerVars.autoplay — belt and
              // suspenders for cases like the card videos, where the
              // player is only created on click and needs to actually
              // start as soon as it can, not just be configured to.
              if (autoplay) player.playVideo();
              pending.forEach((fn) => fn());
              pending.length = 0;
              readyHandlers.forEach((cb) => cb());
            },
            onStateChange: (e) => {
              if (e.data === YT.PlayerState.PLAYING) handlers.play.forEach((cb) => cb());
              else if (e.data === YT.PlayerState.PAUSED) handlers.pause.forEach((cb) => cb());
              else if (e.data === YT.PlayerState.ENDED) handlers.ended.forEach((cb) => cb());
            },
          },
        });
      });
    });

    return {
      play: () => whenReady(() => player.playVideo()),
      pause: () => whenReady(() => player.pauseVideo()),
      mute: () => { mutedState = true; whenReady(() => player.mute()); },
      unmute: () => { mutedState = false; whenReady(() => player.unMute()); },
      isMuted: () => mutedState,
      isPaused: () => (ready ? player.getPlayerState() !== window.YT.PlayerState.PLAYING : true),
      isEnded: () => (ready ? player.getPlayerState() === window.YT.PlayerState.ENDED : false),
      seekToStart: () => whenReady(() => player.seekTo(0, true)),
      getIframe: () => (ready ? player.getIframe() : null),
      onPlay: (cb) => handlers.play.push(cb),
      onPause: (cb) => handlers.pause.push(cb),
      onEnded: (cb) => handlers.ended.push(cb),
      onReady: (cb) => { if (ready) cb(); else readyHandlers.push(cb); },
      // Fully tears the player down (removes its iframe) instead of just
      // pausing it — used when switching which card is playing, so at
      // most one YouTube player for the cards is ever alive at once. See
      // the CARD YOUTUBE PLAYERS section below for why that matters.
      destroy: () => {
        destroyed = true;
        if (player) {
          player.destroy();
          player = null;
          ready = false;
        }
      },
    };
  }

  // ── HERO BACKGROUND VIDEO — FULL-SCREEN PLAY / PAUSE ─────
  function initHeroVideo(section) {
    const nativeVideo = section.querySelector('.bbh-hero-bg-video');
    // .bbh-hero-bg-yt is the persistent wrapper (needed for CSS sizing,
    // since the YouTube API drops the class when it swaps the target
    // div for its iframe). .bbh-hero-bg-yt__target is the inner div that
    // actually gets replaced — it's what carries the data attributes and
    // what gets handed to YT.Player().
    const ytTarget    = section.querySelector('.bbh-hero-bg-yt__target');
    const ytWrapper   = section.querySelector('.bbh-hero-bg-yt');
    const pill        = section.querySelector('.bbh-vid-pill');
    if (!nativeVideo && !ytTarget) return;

    const media = ytTarget
      ? createYouTubeAdapter(ytTarget, ytTarget.dataset.ytId, ytTarget.dataset.ytLoop === 'true')
      : createNativeVideoAdapter(nativeVideo);

    // The maxresdefault thumbnail doesn't exist for every video, and
    // YouTube doesn't 404 when it's missing — it silently serves a tiny
    // 120x90 placeholder instead, so `onerror` never fires. Detect that
    // by its exact dimensions once loaded, and fall back to hqdefault,
    // which YouTube guarantees exists for every video.
    const coverImg = section.querySelector('.bbh-hero-bg-yt__cover-img');
    if (coverImg) {
      const useFallback = () => {
        if (coverImg.naturalWidth === 120 && coverImg.naturalHeight === 90) {
          coverImg.src = `https://img.youtube.com/vi/${coverImg.dataset.ytThumbId}/hqdefault.jpg`;
        }
      };
      if (coverImg.complete) useFallback();
      else coverImg.addEventListener('load', useFallback, { once: true });
    }

    const pillPause = pill ? pill.querySelector('.bbh-vt-pause') : null;
    const pillPlay  = pill ? pill.querySelector('.bbh-vt-play')  : null;
    const pillLabel = pill ? pill.querySelector('.bbh-vt-label') : null;

    // Previously tracked *why* the video was paused, so scrolling the
    // hero back into view could decide whether to auto-resume it. That
    // auto-resume behavior has been removed (see the scroll observer
    // below): once the hero scrolls out of view the video stops for
    // good and only starts again if the visitor deliberately clicks/taps
    // to play it. This flag no longer changes that outcome, but is kept
    // so the click handler and pill still know whether the *visitor*
    // was the one who paused it, for anything that might care later.
    let userPaused = false;

    // On every play()/pause() transition, YouTube's own player briefly
    // flashes a translucent play/pause icon in the center of the video
    // as feedback — this happens even with controls=0 and even though
    // the iframe has pointer-events:none, because it's tied to the
    // state-change itself, not to hover/click. It fires right as the
    // PLAYING state is reported, so revealing the cover the instant we
    // hear "playing" exposes it. Holding the cover a little longer than
    // that flash lasts hides it; re-covering on pause stays instant
    // since there's no flash to hide in that direction.
    let coverRevealTimer = null;
    function updateCover(playing) {
      if (!ytWrapper) return;
      clearTimeout(coverRevealTimer);
      if (playing) {
        coverRevealTimer = setTimeout(() => {
          ytWrapper.classList.add('is-playing');
        }, 800);
      } else {
        ytWrapper.classList.remove('is-playing');
      }
    }

    function sync() {
      const playing = !media.isPaused();
      if (pillPause) pillPause.style.display = playing ? '' : 'none';
      if (pillPlay)  pillPlay.style.display  = playing ? 'none' : '';
      if (pillLabel) pillLabel.textContent   = playing ? 'Playing' : 'Paused';
      if (pill)      pill.classList.toggle('is-paused', !playing);
      updateCover(playing);
    }

    // Entire hero section is the click zone —
    // skip only actual interactive elements (links, buttons)
    section.addEventListener('click', (e) => {
      if (e.target.closest('a, button')) return;
      // First click unmutes (user gesture allows audio)
      if (media.isMuted()) {
        media.unmute();
        syncMute();
        if (media.isPaused()) {
          userPaused = false;
          media.play();
        }
        return;
      }
      if (media.isPaused()) {
        userPaused = false;
        media.play();
      } else {
        userPaused = true;
        media.pause();
      }
    });

    // Show pointer cursor everywhere except over links/buttons
    section.style.cursor = 'pointer';
    section.querySelectorAll('a, button').forEach((el) => {
      el.style.cursor = '';
    });

    media.onPlay(sync);
    media.onPause(sync);
    media.onEnded(sync);

    // ── MUTE / UNMUTE ──────────────────────────────────
    const muteBtn   = section.querySelector('.bbh-vid-mute');
    const mutedIcon = muteBtn ? muteBtn.querySelector('.bbh-vt-muted-icon') : null;
    const soundIcon = muteBtn ? muteBtn.querySelector('.bbh-vt-sound-icon')  : null;

    function syncMute() {
      if (!muteBtn) return;
      const muted = media.isMuted();
      muteBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      muteBtn.classList.toggle('is-unmuted', !muted);
      if (mutedIcon) mutedIcon.style.display = muted ? '' : 'none';
      if (soundIcon) soundIcon.style.display = muted ? 'none' : '';
    }

    if (muteBtn) {
      muteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (media.isMuted()) {
          media.unmute();
        } else {
          media.mute();
        }
        // If user unmutes a paused video, play it
        if (!media.isMuted() && media.isPaused()) {
          userPaused = false;
          media.play();
        }
        syncMute();
      });
    }

    // ── AUTO PAUSE ON SCROLL — NO AUTO-RESUME ───────────
    // Stops the video as soon as the hero scrolls out of view and keeps
    // it stopped — scrolling back up does NOT resume it automatically
    // any more. The video only plays again if the visitor deliberately
    // clicks/taps the hero (or the play control) to start it themselves.
    // This is the ONLY other thing besides the click/mute handlers above
    // that is allowed to call .play()/.pause() on this media — do not
    // add a second, separate script elsewhere that also touches it.
    if ('IntersectionObserver' in window) {
      const scrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && !media.isPaused()) {
              userPaused = true;
              media.pause();
            }
          });
        },
        { threshold: 0.25 }
      );
      scrollObserver.observe(section);
    }

    media.onReady(() => {
      syncMute();
      sync();
    });
  }

  document.querySelectorAll('.bbh-home-hero').forEach(initHeroVideo);

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.bbh-home-hero');
    if (section) initHeroVideo(section);
  });

  // ── FEATURED PRODUCTS / BABES — INLINE CARD VIDEO ────
  // Same click-to-play card pattern is used by both the Featured
  // Products section and the Babes section (both wrap cards in
  // <article>, both use .bbh-pcv-video / [data-pcv-btn]), so this one
  // handler drives both.

  // Sizes a card's YouTube iframe to cover its container regardless of
  // the card's aspect ratio (unlike the hero, cards aren't viewport-
  // sized, so the vh/vw trick used there doesn't apply — this measures
  // the actual container instead).
  function fitYouTubeCoverToContainer(iframe, container) {
    if (!iframe || !container) return;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    if (!cw || !ch) return;
    const videoRatio = 16 / 9;
    const containerRatio = cw / ch;
    let w, h;
    if (containerRatio > videoRatio) {
      w = cw;
      h = cw / videoRatio;
    } else {
      h = ch;
      w = ch * videoRatio;
    }
    iframe.style.width = w + 'px';
    iframe.style.height = h + 'px';
  }

  // Card YouTube iframes that need re-fitting on resize.
  const activeCardYtIframes = [];
  let cardYtResizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(cardYtResizeTimer);
    cardYtResizeTimer = setTimeout(() => {
      activeCardYtIframes.forEach(({ iframe, container }) => fitYouTubeCoverToContainer(iframe, container));
    }, 150);
  });

  // ── CARD YOUTUBE PLAYERS — ONE LIVE INSTANCE AT A TIME ───
  // Two different approaches were tried here before this one. First,
  // one YT.Player per card, all created up front (staggered, then fully
  // serialized) — two videos could still audibly play at once, because
  // simply controlling *when* each player was constructed didn't stop
  // several of them from being alive *simultaneously*, which is what
  // actually seems to trip up YouTube's own IFrame API into misattributing
  // a command or state event to the wrong player. Second, a single
  // shared player repositioned with CSS over whichever card was active —
  // that removed the cross-talk bug, but fighting the theme's own CSS
  // stacking contexts and scroll behavior to keep a floating, JS-
  // positioned element glued correctly to a normal in-flow card turned
  // out to be its own ongoing source of bugs (a card's Play button
  // getting covered, the video overflowing/detaching during scroll).
  //
  // This version keeps each card's video as a normal, permanent part of
  // that card's own DOM — same as before any of this — so there's no
  // floating overlay and nothing to keep positioned. Instead, the never-
  // more-than-one-instance guarantee comes from lifecycle management:
  // the moment a different card is played, the previously active card's
  // YT.Player is fully destroyed (not just paused), before the new
  // card's player is ever constructed. At most one YouTube player for
  // the cards exists at any moment, which is what actually prevents any
  // cross-talk between them — simple, and it doesn't depend on getting
  // any positioning or timing exactly right.
  //
  // Trade-off: only the very first video played on a given page load is
  // pre-buffered for instant playback (see initCardYouTubePlayers).
  // Switching to a *different* card after that briefly re-buffers, the
  // same short delay there was before eager pre-loading was added.

  // Card video ids are read from data-yt-id up front and kept here,
  // because the target div that originally carried that attribute gets
  // consumed (replaced by an iframe, then removed on destroy) the first
  // time a player is built — this is what lets a card be played, then
  // switched away from and destroyed, then played again later.
  const cardYtVideoIds = new WeakMap(); // card -> videoId

  function primeCardYtVideoIds(root) {
    (root || document).querySelectorAll('.bbh-pcv-yt__target').forEach((t) => {
      const card = t.closest('article');
      if (card && t.dataset.ytId) cardYtVideoIds.set(card, t.dataset.ytId);
    });
  }

  const cardMediaAdapters = new WeakMap();
  let activeYouTubeCard = null;

  function destroyCardYouTubePlayer(card) {
    const media = cardMediaAdapters.get(card);
    if (media && media.destroy) {
      const iframe = media.getIframe ? media.getIframe() : null;
      media.destroy();
      if (iframe) {
        const idx = activeCardYtIframes.findIndex((entry) => entry.iframe === iframe);
        if (idx !== -1) activeCardYtIframes.splice(idx, 1);
      }
    }
    cardMediaAdapters.delete(card);
    if (activeYouTubeCard === card) activeYouTubeCard = null;
  }

  function getCardMedia(card) {
    if (cardMediaAdapters.has(card)) return cardMediaAdapters.get(card);

    const nativeVideo = card.querySelector('.bbh-pcv-video:not(.bbh-pcv-yt)');
    const ytWrapper = card.querySelector('.bbh-pcv-yt');
    const videoId = cardYtVideoIds.get(card);
    let media = null;

    if (ytWrapper && videoId) {
      // Tear down whichever other card's player is still alive first —
      // see the comment above this section for why that matters.
      if (activeYouTubeCard && activeYouTubeCard !== card) {
        destroyCardYouTubePlayer(activeYouTubeCard);
      }
      activeYouTubeCard = card;

      // The original .bbh-pcv-yt__target div only exists the very first
      // time a card is played — YT.Player() replaces it with an iframe,
      // and destroy() removes that iframe entirely, leaving nothing
      // behind. Recreate a fresh, empty target div on every subsequent
      // play so there's always somewhere for a new player to attach.
      let ytTarget = card.querySelector('.bbh-pcv-yt__target');
      if (!ytTarget) {
        ytTarget = document.createElement('div');
        ytTarget.className = 'bbh-pcv-yt__target';
        ytWrapper.insertBefore(ytTarget, ytWrapper.firstChild);
      }

      media = createYouTubeAdapter(ytTarget, videoId, true, { autoplay: false, muted: false });

      let revealTimer = null;
      // Same transient-icon issue as the hero: hold the cover a beat
      // after "playing" is confirmed so YouTube's own state-change
      // flash finishes underneath it. See initHeroVideo for the full
      // explanation.
      media.onPlay(() => {
        clearTimeout(revealTimer);
        revealTimer = setTimeout(() => {
          ytWrapper.classList.add('is-yt-playing');
        }, 800);
      });
      media.onPause(() => {
        clearTimeout(revealTimer);
        ytWrapper.classList.remove('is-yt-playing');
      });
      media.onEnded(() => {
        const btn = card.querySelector('[data-pcv-btn]');
        if (btn) stopPcvCard(btn);
      });

      media.onReady(() => {
        const iframe = media.getIframe();
        if (iframe) {
          fitYouTubeCoverToContainer(iframe, ytWrapper);
          activeCardYtIframes.push({ iframe, container: ytWrapper });
        }
      });

      // maxresdefault.jpg doesn't exist for every video, and YouTube
      // serves a tiny 120x90 placeholder instead of a real 404 when
      // it's missing — detect that by its dimensions and fall back to
      // hqdefault.jpg, which always exists.
      const coverImg = card.querySelector('.bbh-pcv-yt__cover-img');
      if (coverImg) {
        const useFallback = () => {
          if (coverImg.naturalWidth === 120 && coverImg.naturalHeight === 90) {
            coverImg.src = `https://img.youtube.com/vi/${coverImg.dataset.ytThumbId}/hqdefault.jpg`;
          }
        };
        if (coverImg.complete) useFallback();
        else coverImg.addEventListener('load', useFallback, { once: true });
      }
    } else if (nativeVideo) {
      media = createNativeVideoAdapter(nativeVideo);
    }

    if (media) cardMediaAdapters.set(card, media);
    return media;
  }

  function stopPcvCard(btn) {
    const card = btn.closest('article');
    if (!card) return;
    const media = cardMediaAdapters.get(card);
    if (media) {
      media.pause();
      media.seekToStart();
    }
    card.classList.remove('is-playing');
    const pi = btn.querySelector('.bbh-pcv-play-icon');
    const si = btn.querySelector('.bbh-pcv-stop-icon');
    const lb = btn.querySelector('.bbh-pcv-label');
    if (pi) pi.style.display = '';
    if (si) si.style.display = 'none';
    if (lb) lb.textContent = btn.dataset.playLabel || 'Play Video';
  }

  // ── STOP CARD VIDEO WHEN ITS SECTION SCROLLS OUT OF VIEW ─
  // A card video only ever starts on a deliberate click (see the click
  // delegation below) — it has no scroll-based autoplay. But once
  // started, it used to just keep playing even after the visitor
  // scrolled the whole .bbh-featured section out of view, and only
  // stopped if they clicked Stop or played a different card. This
  // watches the section itself: the moment it's no longer in view, any
  // card inside it that's currently playing gets stopped (paused and
  // reset), the same as clicking its Stop button. Scrolling back into
  // view does NOT resume it — the visitor has to click Play again,
  // matching how the hero video's scroll behavior works.
  function initFeaturedScrollPause(root) {
    if (!('IntersectionObserver' in window)) return;
    (root || document).querySelectorAll('.bbh-featured').forEach((section) => {
      if (section.dataset.scrollPauseInit) return;
      section.dataset.scrollPauseInit = '1';

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) return;
            section.querySelectorAll('[data-pcv-btn]').forEach((btn) => {
              const card = btn.closest('article');
              if (card && card.classList.contains('is-playing')) {
                stopPcvCard(btn);
              }
            });
          });
        },
        { threshold: 0 }
      );
      observer.observe(section);
    });
  }

  initFeaturedScrollPause(document);

  document.addEventListener('shopify:section:load', (e) => {
    initFeaturedScrollPause(e.target);
  });

  // Warms up the very first card's video at page load so at least the
  // first video anyone plays starts instantly. Every other card buffers
  // normally on its first click — see the trade-off note above.
  function initCardYouTubePlayers(root) {
    primeCardYtVideoIds(root);
    const firstTarget = (root || document).querySelector('.bbh-pcv-yt__target');
    if (!firstTarget) return;
    const card = firstTarget.closest('article');
    if (card) getCardMedia(card);
  }

  initCardYouTubePlayers(document);

  document.addEventListener('shopify:section:load', (e) => {
    initCardYouTubePlayers(e.target);
  });

  // Event delegation — catches all [data-pcv-btn] clicks no matter when rendered
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pcv-btn]');
    if (!btn) return;

    const card = btn.closest('article');
    if (!card) return;
    const media = getCardMedia(card);
    if (!media) return;

    const playing   = card.classList.contains('is-playing');
    const playIcon  = btn.querySelector('.bbh-pcv-play-icon');
    const stopIcon  = btn.querySelector('.bbh-pcv-stop-icon');
    const label     = btn.querySelector('.bbh-pcv-label');
    const playLabel = btn.dataset.playLabel || 'Play Video';

    // Stop all other playing cards on the page
    document.querySelectorAll('[data-pcv-btn]').forEach((other) => {
      if (other !== btn) stopPcvCard(other);
    });

    if (playing) {
      stopPcvCard(btn);
    } else {
      media.play();
      card.classList.add('is-playing');
      if (playIcon) playIcon.style.display = 'none';
      if (stopIcon) stopIcon.style.display = '';
      if (label)    label.textContent = 'Stop';

      // Extra safety net (belt-and-suspenders on top of there now only
      // ever being one live card YouTube player at all — see the CARD
      // YOUTUBE PLAYERS section above): double-check shortly after that
      // no other card is somehow still reporting itself as playing, and
      // force it off if so.
      setTimeout(() => {
        document.querySelectorAll('[data-pcv-btn]').forEach((other) => {
          if (other === btn) return;
          const otherCard = other.closest('article');
          if (!otherCard) return;
          const otherMedia = cardMediaAdapters.get(otherCard);
          if (otherMedia && !otherMedia.isPaused()) {
            stopPcvCard(other);
          }
        });
      }, 600);
    }
  });

  // Reset card when video ends naturally (native <video> only — YouTube
  // cards are always created with loop:true, matching the native cards'
  // fixed `loop` attribute, so ENDED isn't expected there in practice;
  // media.onEnded() below still covers it if that ever changes).
  document.addEventListener('ended', (e) => {
    if (!e.target.classList.contains('bbh-pcv-video')) return;
    const card = e.target.closest('article');
    const btn  = card ? card.querySelector('[data-pcv-btn]') : null;
    if (btn) stopPcvCard(btn);
  }, true);

  // Whole-card click → product page (Featured Products). Clicks on the
  // Play Video button or the Shop Now link handle their own behavior.
  document.addEventListener('click', (e) => {
    const card = e.target.closest('.bbh-product-card[data-card-link]');
    if (!card) return;
    if (e.target.closest('a, button')) return;
    window.location.href = card.dataset.cardLink;
  });

  // ── LOYALTY VIDEO MODAL ───────────────────────────────
  function initLoyaltyModal(root) {
    root.querySelectorAll('[data-loyalty-video]').forEach((btn) => {
      if (btn.dataset.loyaltyInit) return;
      btn.dataset.loyaltyInit = '1';

      const modal = document.getElementById(btn.dataset.modalTarget);
      if (!modal) return;

      const video    = modal.querySelector('.bbh-modal__video:not(.bbh-modal__yt)');
      const ytTarget = modal.querySelector('.bbh-modal__yt__target');
      // Built lazily on first open rather than at page load, so a closed
      // modal never pulls in the YouTube API or spins up a hidden player.
      // Reused on every later open/close of the same modal — once the
      // YouTube API swaps ytTarget for its iframe that div is gone for
      // good, so unlike the product cards there's no "recreate the
      // target" option here; one adapter per modal for the page's life.
      let ytMedia = null;

      function openModal() {
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        modal.querySelector('.bbh-modal__close').focus();
        if (video) {
          video.play().catch(() => {});
        } else if (ytTarget) {
          if (!ytMedia) {
            // Unlike the hero/card players, this one shows YouTube's own
            // controls and is unmuted — it's only ever created in
            // response to a real click, which is exactly the user
            // gesture browsers require to allow unmuted autoplay.
            ytMedia = createYouTubeAdapter(ytTarget, ytTarget.dataset.ytId, false, {
              autoplay: true,
              muted: false,
              controls: true,
              fs: true,
              disablekb: false,
            });
          } else {
            ytMedia.play();
          }
        }
      }

      function closeModal() {
        modal.hidden = true;
        document.body.style.overflow = '';
        if (video) {
          video.pause();
          video.currentTime = 0;
        } else if (ytMedia) {
          ytMedia.pause();
          ytMedia.seekToStart();
        }
        btn.focus();
      }

      btn.addEventListener('click', openModal);

      modal.addEventListener('click', (e) => {
        if (e.target.closest('[data-modal-close]')) closeModal();
      });

      modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeModal();
      });
    });
  }

  initLoyaltyModal(document);

  document.addEventListener('shopify:section:load', (e) => {
    initLoyaltyModal(e.target);
    initReviewsCarousel(e.target);
  });

  // ── REVIEWS CAROUSEL (mobile) ─────────────────────────────
  function initReviewsCarousel(root) {
    const grid = root.querySelector('[data-reviews-grid]');
    const controls = root.querySelector('[data-reviews-controls]');
    if (!grid || !controls) return;

    const cards = Array.from(grid.querySelectorAll('.bbh-review'));
    const dots  = Array.from(controls.querySelectorAll('[data-reviews-dot]'));
    const prev  = controls.querySelector('[data-reviews-prev]');
    const next  = controls.querySelector('[data-reviews-next]');

    if (!cards.length) return;

    let current = 0;

    function goTo(index) {
      current = Math.max(0, Math.min(index, cards.length - 1));
      grid.scrollTo({ left: cards[current].offsetLeft, behavior: 'smooth' });
      dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
    }

    prev && prev.addEventListener('click', () => goTo(current - 1));
    next && next.addEventListener('click', () => goTo(current + 1));
    dots.forEach((d) => d.addEventListener('click', () => goTo(Number(d.dataset.reviewsDot))));

    // Sync dots when user swipes manually
    let scrollTimer;
    grid.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const cardWidth = cards[0].offsetWidth;
        const idx = Math.round(grid.scrollLeft / cardWidth);
        current = Math.max(0, Math.min(idx, cards.length - 1));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
      }, 80);
    });
  }

  initReviewsCarousel(document);

});